import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  Card,
  EmptyState,
  FIELD,
  FILL_BODY,
  FILL_CARD,
  FILL_PAGE,
  PageHeader,
  Pagination,
  ROW_SCROLL,
  STICKY_THEAD,
  usePageSize,
} from "../components/account-ui.tsx";
import { useAppDm } from "../components/legacy/app-state.tsx";
import { UserAvatar } from "../components/UserAvatar.tsx";
import { UserIcon } from "../icons/index.tsx";
import { type AppConfig, api, type Customer } from "../lib/api.ts";
import { isStaffRole, splitName } from "../lib/legacy.ts";

export const Route = createFileRoute("/app/$customerId/allcustomers")({
  component: AllCustomers,
  beforeLoad: async ({ context, params }) => {
    const config = await context.queryClient.ensureQueryData({
      queryKey: ["config"],
      queryFn: () => api.get<AppConfig>("/config"),
    });
    if (!config.user || !isStaffRole(config.user.role)) {
      throw redirect({
        to: "/app/$customerId/mycampaigns",
        params: { customerId: params.customerId },
      });
    }
  },
});

const OUTLINE =
  "inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50";

function AllCustomers() {
  const dm = useAppDm();
  const navigate = useNavigate();
  const { ui, set, customerId, adminMode } = dm;
  const [q, setQ] = useState("");
  const [perPage, setPerPage] = usePageSize();
  const listKey = JSON.stringify([q, perPage]);
  const [paged, setPaged] = useState({ key: listKey, n: 0 });

  useEffect(() => {
    if (ui.appContext !== "customers") set({ appContext: "customers" });
  }, [ui.appContext, set]);

  const { data } = useQuery({
    queryKey: ["customers", q],
    queryFn: () =>
      api.get<{ customers: Customer[] }>(
        `/admin/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      ),
  });

  const showOrSelectCustomer = (id: string) => {
    set({ appContext: "myCampaigns" });
    navigate({ to: "/app/$customerId/mycampaigns", params: { customerId: id } });
  };

  const customers = data?.customers ?? [];
  const page = Math.min(
    paged.key === listKey ? paged.n : 0,
    Math.max(0, Math.ceil(customers.length / perPage) - 1),
  );
  const shown = customers.slice(page * perPage, (page + 1) * perPage);
  const showIds = adminMode && ui.showIDs;

  return (
    <div className={FILL_PAGE}>
      <div className="shrink-0">
        <PageHeader
          title="Customers"
          subtitle="Open a customer to work in their campaigns."
          action={
            <button
              type="button"
              className={OUTLINE}
              onClick={() =>
                navigate({ to: "/app/$customerId/mycampaigns", params: { customerId } })
              }
            >
              My Campaigns
            </button>
          }
        />

        <div className="mb-4 max-w-md">
          <label className="sr-only" htmlFor="customer-search">
            Search customers
          </label>
          <input
            id="customer-search"
            type="search"
            placeholder="Search customers…"
            className={FIELD}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <Card className={FILL_CARD} bodyClass={FILL_BODY}>
        {data && customers.length === 0 ? (
          <EmptyState
            Icon={UserIcon}
            title="No customers match"
            message={q ? `Nothing found for “${q}”.` : "No customers yet."}
          />
        ) : (
          <>
            <div className={ROW_SCROLL}>
              <table className="w-full text-left text-sm">
                <thead className={STICKY_THEAD}>
                  <tr>
                    {showIds && <th className="px-5 py-3 font-semibold">Customer ID</th>}
                    <th className="px-5 py-3 font-semibold">Customer</th>
                    <th className="px-5 py-3 font-semibold">Campaigns</th>
                    <th className="px-5 py-3 text-right font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {shown.map((customer) => {
                    const name = splitName(customer.name);
                    return (
                      <tr
                        key={customer.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => showOrSelectCustomer(customer.id)}
                      >
                        {showIds && (
                          <td className="whitespace-nowrap px-5 py-4 font-mono text-xs text-gray-500">
                            {customer.id}
                          </td>
                        )}
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <UserAvatar
                              name={customer.name}
                              image={customer.image}
                              updatedAt={customer.updatedAt}
                              role={customer.role}
                              size="sm"
                              decorative
                            />
                            <div className="min-w-0">
                              <p className="flex items-center gap-2 font-medium text-gray-900">
                                <span className="truncate">
                                  {name.firstName} {name.lastName}
                                </span>
                                {isStaffRole(customer.role) && (
                                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-800">
                                    {customer.role}
                                  </span>
                                )}
                              </p>
                              {customer.organisation && (
                                <p className="truncate text-xs text-gray-500">
                                  {customer.organisation}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 tabular-nums text-gray-600">
                          {customer.campaigns ?? 0}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              className={OUTLINE}
                              onClick={(e) => {
                                e.stopPropagation();
                                showOrSelectCustomer(customer.id);
                              }}
                            >
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={perPage}
              total={customers.length}
              onPage={(n) => setPaged({ key: listKey, n })}
              onPageSize={setPerPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
